import { IconMapMarker } from '@/icons/IconMapMarker';
import { IconPhone } from '@/icons/IconPhone';
import { IStoreLists } from '@/interfaces/store';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MapBox, { GeolocateControl, MapRef, Marker, NavigationControl, Popup } from 'react-map-gl/mapbox';

const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1Ijoic2JodWl5YW4iLCJhIjoiY2tiMHd1ODZzMGM3czJ5cWplcjZ3Nmw5diJ9.kqpTrvy85RG81TAZGJ_m7Q';

// Default center: Dhaka, Bangladesh
const DEFAULT_CENTER = { longitude: 90.4125, latitude: 23.8103, zoom: 11 };

type MarkersProps = {
  locations?: IStoreLists[];
  selectedLocation?: IStoreLists;
  setSelectedLocation?: (loc: IStoreLists) => void;
  setPopupInfo: (info: IStoreLists) => void;
  /** Notify parent of an active marker click (gates programmatic popup reopen). */
  onUserSelect?: () => void;
};

const Markers = React.memo(function Markers({
  locations,
  selectedLocation,
  setSelectedLocation,
  setPopupInfo,
  onUserSelect,
}: MarkersProps) {
  const handleMarkerClick = useCallback(
    (location: IStoreLists) => (e: any) => {
      e.originalEvent?.stopPropagation?.();
      setPopupInfo(location);
      setSelectedLocation?.(location);
      onUserSelect?.();
    },
    [setPopupInfo, setSelectedLocation, onUserSelect],
  );

  return (
    <>
      {locations?.map((location, index) => (
        <Marker
          key={location?.Id ?? `marker_${index}`}
          longitude={location?.Longitude}
          latitude={location?.Latitude}
          anchor="top"
          onClick={handleMarkerClick(location)}
        >
          <div className={`${!selectedLocation ? '' : location === selectedLocation ? 'selected' : ''}`}>
            <IconMapMarker className="w-7 cursor-pointer text-red-500" style={{ filter: 'drop-shadow(2px 2px 2px rgb(0 0 0 / 30%))' }} />
          </div>
        </Marker>
      ))}
    </>
  );
});

const Map = ({
  locations,
  className,
  selectedLocation,
  setSelectedLocation,
  cooperativeGestures = true,
  initialCenter,
  pickedLocation,
  onPickLocation,
  onMapClick,
  pickOnClick,
  pickedPopup,
  hidePickedMarker = false,
  centroids,
}: {
  locations?: IStoreLists[];
  className?: string;
  selectedLocation?: IStoreLists[];
  setSelectedLocation?: (loc: IStoreLists) => void;
  cooperativeGestures?: boolean;
  initialCenter?: { longitude: number; latitude: number; zoom?: number };
  pickedLocation?: { longitude: number; latitude: number } | null;
  onPickLocation?: (loc: { longitude: number; latitude: number }) => void;
  onMapClick?: (coords: { latitude: number; longitude: number }) => void;
  pickOnClick?: boolean;
  pickedPopup?: { isVisible: boolean; render: () => React.ReactNode } | null;
  hidePickedMarker?: boolean;
  centroids?: Array<{
    Id: string;
    Name: string;
    Longitude: number;
    Latitude: number;
    selected?: boolean;
  }>;
}) => {
  const [popupInfo, setPopupInfo] = useState<IStoreLists | null>(null);

  const mapRef = useRef<MapRef | null>(null);
  const fitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Gates the programmatic-selection popup-reopen effect.
  const userSelectedRef = useRef<boolean>(false);
  // Suppresses redundant camera moves when re-selecting the same store.
  const lastFlownSelectedIdRef = useRef<string | number | null>(null);
  const lastListTargetFingerprintRef = useRef<string>('');

  // explicit initialCenter wins, else pickedLocation, else Dhaka
  const effectiveCenter = initialCenter || (pickedLocation ? { ...pickedLocation, zoom: 13 } : DEFAULT_CENTER);

  /** Thin wrapper around flyTo kept for symmetry with future animation hooks. */
  const startFlyTo = (center: [number, number], zoom: number, duration: number) => {
    mapRef.current?.flyTo({ center, duration, zoom });
  };

  useEffect(() => {
    if (!locations || locations.length === 0) {
      return;
    }
    if (pickedLocation) {
      const selected = selectedLocation?.[0];
      const pickedMatchesSelection =
        selected &&
        Number(pickedLocation.longitude) === Number(selected.Longitude) &&
        Number(pickedLocation.latitude) === Number(selected.Latitude);
      if (!pickedMatchesSelection) {
        return;
      }
    }
    const listFingerprint = locations.map((l) => l?.Id).filter(Boolean).join('|');
    const selected = selectedLocation?.[0];
    const target =
      selected && locations.some((l) => l?.Id === selected.Id) ? selected : locations[0];
    const targetFingerprint = `${target?.Id ?? ''}|${listFingerprint}`;
    if (targetFingerprint === lastListTargetFingerprintRef.current) {
      return;
    }
    lastListTargetFingerprintRef.current = targetFingerprint;

    if (fitTimerRef.current) {
      clearTimeout(fitTimerRef.current);
    }
    fitTimerRef.current = setTimeout(() => {
      startFlyTo(
        [target.Longitude as number, target.Latitude as number],
        13,
        1200,
      );
    }, 200);
    return () => {
      if (fitTimerRef.current) {
        clearTimeout(fitTimerRef.current);
        fitTimerRef.current = null;
      }
    };
  }, [locations, selectedLocation, pickedLocation]);

  // Re-center on selection change; id-change gate prevents camera churn on re-select.
  useEffect(() => {
    const selected = selectedLocation?.[0];
    if (!selected?.Id) {
      lastFlownSelectedIdRef.current = null;
      return;
    }
    if (lastFlownSelectedIdRef.current === selected.Id) {
      return;
    }
    lastFlownSelectedIdRef.current = selected.Id;
    startFlyTo(
      [selected.Longitude as number, selected.Latitude as number],
      14,
      1500,
    );
  }, [selectedLocation]);

  // Open popup only when the user actively clicked a marker and selection changed.
  useEffect(() => {
    const selected = selectedLocation?.[0];
    if (
      userSelectedRef.current &&
      selected &&
      popupInfo?.Id !== selected.Id
    ) {
      setPopupInfo(selected);
    }
    if (!selectedLocation || selectedLocation.length === 0) {
      userSelectedRef.current = false;
      setPopupInfo(null);
    }
  }, [selectedLocation, popupInfo]);

  // Fly to picked location; on reset, fly back to initialCenter because initialViewState is mount-only.
  useEffect(() => {
    if (pickedLocation) {
      startFlyTo([pickedLocation.longitude, pickedLocation.latitude], 13, 1000);
      return;
    }
    const fallback = (locations && locations.length > 0)
      ? { longitude: locations[0].Longitude as number, latitude: locations[0].Latitude as number, zoom: 13 }
      : initialCenter ?? DEFAULT_CENTER;
    startFlyTo([fallback.longitude, fallback.latitude], fallback.zoom ?? 13, 1000);
  }, [pickedLocation, locations, initialCenter]);

  return (
    <div className={`relative h-full w-full overflow-hidden ${className ?? ''}`} onWheel={(e) => e.stopPropagation()}>
      <MapBox
        ref={mapRef}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        cooperativeGestures={cooperativeGestures}
        attributionControl={false}
        initialViewState={effectiveCenter}
        onClick={(e: any) => {
          if (popupInfo) {
            setPopupInfo(null);
          }
          if (onMapClick && pickOnClick !== false && e?.lngLat) {
            onMapClick({ latitude: e.lngLat.lat, longitude: e.lngLat.lng });
          }
        }}
      >
        <GeolocateControl
          position="bottom-right"
          positionOptions={{ enableHighAccuracy: true, timeout: 6000 }}
          fitBoundsOptions={{ maxZoom: 14, padding: 64 }}
        />
        <NavigationControl position="bottom-right" showCompass={false} />
        <Markers
          setSelectedLocation={setSelectedLocation}
          locations={locations}
          selectedLocation={selectedLocation?.[0]}
          setPopupInfo={setPopupInfo}
          onUserSelect={() => {
            userSelectedRef.current = true;
          }}
        />
        {centroids?.map((c, index) => (
          <Marker
            key={c?.Id ?? `centroid_${index}`}
            longitude={c?.Longitude}
            latitude={c?.Latitude}
            anchor="center"
          >
            <div
              className={
                c?.selected
                  ? 'h-3 w-3 rounded-full bg-red-500 opacity-90'
                  : 'h-2 w-2 rounded-full bg-blue-500 opacity-60'
              }
              style={{ filter: 'drop-shadow(1px 1px 1px rgb(0 0 0 / 30%))' }}
              aria-label={c?.Name}
            />
          </Marker>
        ))}
        {pickedLocation && !hidePickedMarker && (
          <Marker
            longitude={pickedLocation.longitude}
            latitude={pickedLocation.latitude}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent?.stopPropagation?.();
              if (onMapClick && pickOnClick !== false) {
                onMapClick({ latitude: pickedLocation.latitude, longitude: pickedLocation.longitude });
              }
            }}
          >
            <IconMapMarker className="w-8 text-blue-600" style={{ filter: 'drop-shadow(2px 4px 4px rgb(0 0 0 / 50%))' }} />
          </Marker>
        )}
        {pickedLocation && !hidePickedMarker && pickedPopup?.isVisible && (
          <Popup
            style={{ filter: 'drop-shadow(2px 2px 8px rgb(0 0 0 / 25%))' }}
            anchor="bottom"
            longitude={pickedLocation.longitude}
            latitude={pickedLocation.latitude}
            onClose={() => {}}
          >
            {pickedPopup.render()}
          </Popup>
        )}
        {popupInfo && (
          <Popup
            style={{ filter: 'drop-shadow(2px 2px 8px rgb(0 0 0 / 25%))' }}
            anchor="bottom"
            longitude={popupInfo?.Longitude}
            latitude={popupInfo?.Latitude}
            onClose={() => {
              // Close popup only; reset the gate so the selection effect doesn't reopen it.
              userSelectedRef.current = false;
              setPopupInfo(null);
            }}
          >
            <p className="mb-3">{popupInfo?.Address}</p>
            <p>{popupInfo?.OpeningDays}</p>
            <p>{popupInfo?.OpeningHours}</p>
            {popupInfo?.PhoneNumber && (
              <p className="mt-3 text-lg">
                <a href={`tel:${popupInfo?.PhoneNumber}`} className="group tracking-wider">
                  <IconPhone className="icon mr-1 w-6 rounded-full border-2 border-blue-500 p-0.5 text-blue-500 group-hover:rotate-45" />
                  {popupInfo?.PhoneNumber}
                </a>
              </p>
            )}
          </Popup>
        )}
      </MapBox>

      <style jsx global>{`
        .mapboxgl-map {
          font-family: inherit;
          font-size: 13px;
          line-height: 1.5;
        }

        // Force NavigationControl zoom buttons visible (override global rule).
        .mapboxgl-ctrl.mapboxgl-ctrl-group {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        .mapboxgl-ctrl-bottom-right {
          display: block !important;
          visibility: visible !important;
        }

        // Override Tailwind preflight (rounded-full + before:animate-spin) which hides mapbox +/- icons.
        .mapboxgl-ctrl-group button,
        .mapboxgl-ctrl button {
          background: #fff !important;
          border-radius: 0 !important;
        }
        .mapboxgl-ctrl-group button::before,
        .mapboxgl-ctrl button::before {
          content: none !important;
          animation: none !important;
        }
        .mapboxgl-ctrl-group button.mapboxgl-ctrl-zoom-in {
          border-radius: 4px 4px 0 0 !important;
        }
        .mapboxgl-ctrl-group button.mapboxgl-ctrl-zoom-out {
          border-radius: 0 0 4px 4px !important;
        }

        .mapboxgl-popup-content {
          padding: 18px 18px 15px;
        }

        .mapboxgl-popup-close-button {
          position: absolute !important;
          top: 6px !important;
          right: 6px !important;
          width: 28px !important;
          height: 28px !important;
          padding: 0 !important;
          margin: 0 !important;
          border: 0 !important;
          border-radius: 9999px !important;
          background: rgba(255, 255, 255, 0.9) !important;
          color: #111 !important;
          font-size: 20px !important;
          line-height: 24px !important;
          text-indent: 0 !important;
          text-align: center !important;
          cursor: pointer !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          outline: none !important;
          transition: background-color 0.15s ease, color 0.15s ease;
          z-index: 2;
        }

        .mapboxgl-popup-close-button:hover {
          background: #fef2f2 !important;
          color: #dc2626 !important;
        }

        .mapboxgl-popup-close-button:focus-visible {
          box-shadow: 0 0 0 2px #dc2626 !important;
        }

        // react-map-gl already renders an × in this button; hide the extra one.
        .mapboxgl-popup-close-button::before {
          content: none !important;
          animation: none !important;
        }

        .mapboxgl-marker .selected {
          filter: drop-shadow(2px 2px 2px rgb(0 0 0 / 50%));
        }
        .mapboxgl-marker .selected svg {
          color: #b91c1c;
          transform: scale(1.4);
        }
      `}</style>
    </div>
  );
};

export default Map;
